export interface ThemeOrigin {
  x: number;
  y: number;
}

export function elementCenter(element: HTMLElement): ThemeOrigin {
  const rect = element.getBoundingClientRect();
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  };
}

const THEME_TRANSITION_DURATION_VAR = "--duration-s-theme";
const THEME_TRANSITION_EASE_VAR = "--ease-s-standard";
const THEME_TRANSITION_PSEUDO = "::view-transition-new(root)";
const THEME_SWITCHING_ATTRIBUTE = "data-theme-switching";

function readCustom(styles: CSSStyleDeclaration, name: string): string {
  let raw = styles.getPropertyValue(name).trim();
  for (let hop = 0; hop < 4 && raw.startsWith("var("); hop += 1) {
    const body = raw.endsWith(")") ? raw.slice(4, -1) : raw.slice(4);
    const referenced = body.split(",")[0]?.trim() ?? "";
    raw = styles.getPropertyValue(referenced).trim();
  }
  return raw;
}

function readDurationMs(styles: CSSStyleDeclaration): number {
  const raw = readCustom(styles, THEME_TRANSITION_DURATION_VAR);
  const value = Number.parseFloat(raw);
  if (Number.isNaN(value)) return 0;
  if (raw.endsWith("ms")) return value;
  if (raw.endsWith("s")) return value * 1000;
  return 0;
}

export function themeRevealFrames(
  origin: ThemeOrigin | undefined,
  width: number,
  height: number,
): readonly [string, string] {
  const x = origin?.x ?? width / 2;
  const y = origin?.y ?? height / 2;
  const farthest = Math.hypot(Math.max(x, width - x), Math.max(y, height - y));
  const at = `at ${(x / width) * 100}% ${(y / height) * 100}%`;
  const radiusPercent =
    (farthest / (Math.hypot(width, height) / Math.SQRT2)) * 100;
  return [`circle(0% ${at})`, `circle(${radiusPercent}% ${at})`];
}

function releaseTransitions(root: HTMLElement) {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() =>
      root.removeAttribute(THEME_SWITCHING_ATTRIBUTE),
    );
  });
}

export function runThemeTransition(update: () => void, origin?: ThemeOrigin) {
  const root = document.documentElement;
  const frozenUpdate = () => {
    root.setAttribute(THEME_SWITCHING_ATTRIBUTE, "");
    update();
  };

  const styles = getComputedStyle(root);
  const duration = readDurationMs(styles);
  const width = window.innerWidth;
  const height = window.innerHeight;
  if (
    typeof document.startViewTransition !== "function" ||
    duration <= 0 ||
    width <= 0 ||
    height <= 0
  ) {
    frozenUpdate();
    releaseTransitions(root);
    return;
  }

  let transition: ViewTransition;
  try {
    transition = document.startViewTransition(frozenUpdate);
  } catch {
    frozenUpdate();
    releaseTransitions(root);
    return;
  }
  void transition.finished.finally(() => releaseTransitions(root));
  void transition.ready.then(
    () => {
      root.animate(
        { clipPath: [...themeRevealFrames(origin, width, height)] },
        {
          duration,
          easing: readCustom(styles, THEME_TRANSITION_EASE_VAR) || "linear",
          pseudoElement: THEME_TRANSITION_PSEUDO,
        },
      );
    },
    () => undefined,
  );
}
